import Component from '@glimmer/component';

import moment from 'moment';

import { getVerificationEntityDisplayName, isVerified } from 'webapp/utils/verification';
import type {
	VerificationBadgeSize,
	VerificationEntityLabel,
	VerificationInfo,
} from 'webapp/utils/verification';

import CommonUiIcon from 'webapp/components/common/ui-icon';
import ToolTip from 'webapp/components/tool-tip';

interface Args {
	verification?: VerificationInfo;
	size?: VerificationBadgeSize;
	showTooltip?: boolean;
	entityLabel?: VerificationEntityLabel;
}

const UNKNOWN_USER = 'an unknown user';

export default class VerificationBadgeComponent extends Component<Args> {
	get isVerified(): boolean {
		return isVerified(this.args.verification);
	}

	get size(): VerificationBadgeSize {
		return this.args.size ?? 'sm';
	}

	get showTooltip(): boolean {
		return this.args.showTooltip ?? true;
	}

	get entityLabel(): VerificationEntityLabel {
		return this.args.entityLabel ?? 'dataset';
	}

	private get description(): string {
		const verification = this.args.verification;
		const timestamp = verification?.modified ?? verification?.created;
		if (!timestamp) {
			return `This ${getVerificationEntityDisplayName(this.entityLabel)} has been verified.`;
		}

		const actor = verification?.modifiedBy ?? verification?.createdBy;
		const userName = actor?.name ?? UNKNOWN_USER;
		const m = moment(timestamp);
		const relative = m.fromNow();
		const absolute = m.format('MMMM D, YYYY [at] h:mma');

		return `This ${getVerificationEntityDisplayName(this.entityLabel)} was last verified by ${userName} ${relative} (${absolute}).`;
	}

	get tooltipText(): string {
		const tooltipTitle = `Verified ${getVerificationEntityDisplayName(this.entityLabel)}`;
		return (
			`<div class="verification-tooltip">` +
			`<div class="verification-tooltip__header">` +
			`<ui-icon name="VerifiedFill" size="md"></ui-icon>` +
			`<span class="verification-tooltip__title">${tooltipTitle}</span>` +
			`</div>` +
			`<div class="verification-tooltip__description">${this.description}</div>` +
			`</div>`
		);
	}

	<template>
		{{#if this.isVerified}}
			<span class="verification-badge verification-badge--{{this.size}}">
				<CommonUiIcon @name="VerifiedFill" @size={{this.size}} />

				{{#if this.showTooltip}}
					<ToolTip
						@text={{this.tooltipText}}
						@position="top"
						@maxWidth={{360}}
						@delay={{200}}
						@theme="verification"
					/>
				{{/if}}
			</span>
		{{/if}}
	</template>
}
